import * as Sentry from '@sentry/react';
import { ENV_PRODUCTION } from '@common/config/environments';

interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  additionalData?: Record<string, any>;
}

class GlobalErrorHandler {
  private isInitialized = false;

  /**
   * Initialize global error handlers
   */
  public initialize(): void {
    if (this.isInitialized) {
      return;
    }

    // Handle unhandled JavaScript errors
    window.addEventListener('error', this.handleJavaScriptError.bind(this));

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', this.handlePromiseRejection.bind(this));

    // Handle resource loading errors
    window.addEventListener('error', this.handleResourceError.bind(this), true);

    // Handle console errors (in development)
    if (!ENV_PRODUCTION) {
      this.overrideConsoleError();
    }

    this.isInitialized = true;
    console.log('Global error handlers initialized');
  }

  /**
   * Handle JavaScript errors
   */
  private handleJavaScriptError(event: ErrorEvent): void {
    const { error, filename, lineno, colno, message } = event;

    Sentry.withScope((scope) => {
      scope.setTag('errorType', 'javascript');
      scope.setLevel('error');
      
      scope.setContext('errorEvent', {
        filename,
        lineno,
        colno,
        message,
        timestamp: new Date().toISOString()
      });

      // Add user context
      this.addUserContext(scope);

      // Check for TypeError specifically
      if (error && error instanceof TypeError) {
        scope.setTag('errorCategory', 'typeError');
        scope.setContext('typeError', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
      }

      // Check for chunk load errors
      if (message.includes('Loading chunk') || message.includes('ChunkLoadError')) {
        scope.setTag('errorCategory', 'chunkLoadError');
        scope.setContext('chunkLoadError', {
          message,
          filename,
          timestamp: new Date().toISOString()
        });
      }

      Sentry.captureException(error || new Error(message));
    });
  }

  /**
   * Handle unhandled promise rejections
   */
  private handlePromiseRejection(event: PromiseRejectionEvent): void {
    Sentry.withScope((scope) => {
      scope.setTag('errorType', 'promiseRejection');
      scope.setLevel('error');

      scope.setContext('promiseRejection', {
        reason: event.reason,
        timestamp: new Date().toISOString()
      });

      // Add user context
      this.addUserContext(scope);

      // Check if it's a TypeError in promise rejection
      if (event.reason instanceof TypeError) {
        scope.setTag('errorCategory', 'typeError');
        scope.setContext('typeError', {
          name: event.reason.name,
          message: event.reason.message,
          stack: event.reason.stack
        });
      }

      Sentry.captureException(event.reason);
    });
  }

  /**
   * Handle resource loading errors (images, scripts, etc.)
   */
  private handleResourceError(event: ErrorEvent): void {
    const target = event.target as HTMLElement;
    
    if (target && (target.tagName === 'IMG' || target.tagName === 'SCRIPT' || target.tagName === 'LINK')) {
      Sentry.withScope((scope) => {
        scope.setTag('errorType', 'resourceLoadError');
        scope.setLevel('warning');

        scope.setContext('resourceError', {
          tagName: target.tagName,
          src: (target as any).src || (target as any).href,
          timestamp: new Date().toISOString()
        });

        // Add user context
        this.addUserContext(scope);

        Sentry.captureMessage(`Resource load error: ${target.tagName}`, 'warning');
      });
    }
  }

  /**
   * Override console.error in development to capture errors
   */
  private overrideConsoleError(): void {
    const originalConsoleError = console.error;
    
    console.error = (...args: any[]) => {
      // Call original console.error
      originalConsoleError.apply(console, args);

      // Capture in Sentry if it looks like an error
      if (args.length > 0 && typeof args[0] === 'string' && args[0].includes('Error')) {
        Sentry.withScope((scope) => {
          scope.setTag('errorType', 'consoleError');
          scope.setLevel('error');
          
          scope.setContext('consoleError', {
            message: args.join(' '),
            timestamp: new Date().toISOString()
          });

          this.addUserContext(scope);

          Sentry.captureMessage(args.join(' '), 'error');
        });
      }
    };
  }

  /**
   * Add user context to Sentry scope
   */
  private addUserContext(scope: Sentry.Scope): void {
    const token = localStorage.getItem('token');
    
    if (token) {
      scope.setTag('userAuthenticated', 'true');
      // Note: Don't send PII unless explicitly configured
      // You could decode JWT token here to get user info
      // but be careful not to send sensitive information
    } else {
      scope.setTag('userAuthenticated', 'false');
    }
  }

  /**
   * Manually capture an error with additional context
   */
  public captureError(error: Error, context?: ErrorContext): void {
    Sentry.withScope((scope) => {
      scope.setLevel('error');
      
      if (context?.component) {
        scope.setTag('component', context.component);
      }
      
      if (context?.action) {
        scope.setTag('action', context.action);
      }

      if (context?.additionalData) {
        scope.setContext('additionalData', context.additionalData);
      }

      // Add user context
      this.addUserContext(scope);

      // Check for TypeError
      if (error instanceof TypeError) {
        scope.setTag('errorCategory', 'typeError');
        scope.setContext('typeError', {
          name: error.name,
          message: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString()
        });
      }

      Sentry.captureException(error);
    });
  }

  /**
   * Capture a message with context
   */
  public captureMessage(message: string, level: Sentry.SeverityLevel = 'error', context?: ErrorContext): void {
    Sentry.withScope((scope) => {
      scope.setLevel(level);
      
      if (context?.component) {
        scope.setTag('component', context.component);
      }
      
      if (context?.action) {
        scope.setTag('action', context.action);
      }

      if (context?.additionalData) {
        scope.setContext('additionalData', context.additionalData);
      }

      this.addUserContext(scope);

      Sentry.captureMessage(message, level);
    });
  }

  /**
   * Add breadcrumb for debugging
   */
  public addBreadcrumb(message: string, category: string, level: Sentry.SeverityLevel = 'info', data?: Record<string, any>): void {
    Sentry.addBreadcrumb({
      message,
      category,
      level,
      data,
      timestamp: Date.now() / 1000
    });
  }
}

// Export singleton instance
export const globalErrorHandler = new GlobalErrorHandler();

// Export types
export type { ErrorContext };

